import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import ImageGalleryDialog from './ImageGalleryDialog';

const ImagePreviewContext = createContext(null);

const INTERACTIVE_SELECTOR = [
  'a',
  'button',
  'input',
  'label',
  'select',
  'textarea',
  '[role="button"]',
  '[role="link"]',
  '[tabindex]:not([tabindex="-1"])',
  '.MuiButtonBase-root',
].join(',');

const getReactProps = (element) => {
  const propsKey = Object.keys(element || {}).find((key) => key.startsWith('__reactProps$'));
  return propsKey ? element[propsKey] : null;
};

const hasReactClickHandler = (element, stopAt) => {
  let current = element;
  while (current && current !== document.body && current !== stopAt) {
    const props = getReactProps(current);
    if (typeof props?.onClick === 'function') return true;
    current = current.parentElement;
  }
  return false;
};

const hasPointerCursor = (element, stopAt) => {
  let current = element;
  while (current && current !== document.body && current !== stopAt) {
    if (window.getComputedStyle(current).cursor === 'pointer') return true;
    current = current.parentElement;
  }
  return false;
};

const isExistingFullPreviewImage = (img) => {
  const alt = img.getAttribute('alt') || '';
  return alt.startsWith('Face for ') || alt === 'Recognition';
};

const shouldSkipImagePreview = (img) => {
  if (!img?.src) return true;
  if (img.closest('[data-no-image-preview="true"]')) return true;
  if (img.closest('[data-image-preview-dialog="true"]')) return true;
  if (isExistingFullPreviewImage(img)) return true;
  if (img.closest('[data-image-preview-force="true"]')) return false;

  const src = img.getAttribute('src') || '';
  if (!src || src.startsWith('#')) return true;
  if (src.includes('/assets/icons/') || src.includes('view-image-icon') || src.includes('edit-image-icon')) return true;

  const nearestInteractive = img.closest(INTERACTIVE_SELECTOR);
  if (nearestInteractive) return true;

  const nearestDialog = img.closest('[role="dialog"]');
  if (hasReactClickHandler(img, nearestDialog)) return true;
  if (hasPointerCursor(img, nearestDialog)) return true;

  return false;
};

const getImageTitle = (img) => (
  img.getAttribute('data-preview-title') ||
  img.getAttribute('alt') ||
  img.getAttribute('aria-label') ||
  'Image Preview'
);

const isPreviewableGroupedImage = (img) => {
  if (!img?.src) return false;
  if (img.closest('[data-no-image-preview="true"]')) return false;
  if (img.closest('[data-image-preview-dialog="true"]')) return false;
  const src = img.currentSrc || img.src || img.getAttribute('src') || '';
  if (!src || src.startsWith('#')) return false;
  return !(src.includes('/assets/icons/') || src.includes('view-image-icon') || src.includes('edit-image-icon'));
};

const collectImageGroup = (img) => {
  const fallback = {
    images: [{
      src: img.currentSrc || img.src,
      title: getImageTitle(img),
      alt: img.getAttribute('alt') || getImageTitle(img),
    }],
    index: 0,
  };
  const group = img.closest('[data-image-preview-group]');
  if (!group) return fallback;

  const images = Array.from(group.querySelectorAll('img'))
    .filter(isPreviewableGroupedImage)
    .map((groupImg) => ({
      src: groupImg.currentSrc || groupImg.src,
      title: getImageTitle(groupImg),
      alt: groupImg.getAttribute('alt') || getImageTitle(groupImg),
    }))
    .filter((item) => item.src);

  if (!images.length) return fallback;
  const clickedSrc = img.currentSrc || img.src;
  const index = images.findIndex((item) => item.src === clickedSrc);
  return { images, index: index >= 0 ? index : 0 };
};

export const ImagePreviewDialog = ({
  open,
  src,
  title = 'Image Preview',
  alt,
  images = null,
  initialIndex = 0,
  employeeName = '',
  employeeId = '',
  employeeImage = '',
  hideTitle = false,
  resolveImageSrc = null,
  onClose,
}) => (
  <ImageGalleryDialog
    isOpen={open}
    onClose={onClose}
    src={src}
    title={title}
    alt={alt}
    images={images}
    initialImageIndex={initialIndex}
    employeeName={employeeName}
    employeeId={employeeId}
    profileImage={employeeImage}
    hideTitle={hideTitle}
    resolveImageSrc={resolveImageSrc}
  />
);

export const ImagePreviewProvider = ({ children }) => {
  const [preview, setPreview] = useState({
    open: false,
    src: '',
    title: '',
    alt: '',
    images: [],
    index: 0,
    employeeName: '',
    employeeId: '',
    employeeImage: '',
    hideTitle: false,
    resolveImageSrc: null,
  });

  const closeImagePreview = useCallback(() => {
    setPreview((prev) => ({ ...prev, open: false }));
  }, []);

  const openImagePreview = useCallback(({
    src,
    title,
    alt,
    images,
    index = 0,
    employeeName = '',
    employeeId = '',
    employeeImage = '',
    hideTitle = false,
    resolveImageSrc = null,
  } = {}) => {
    const galleryImages = Array.isArray(images) ? images : [];
    const hasLazyGallery = typeof resolveImageSrc === 'function' && galleryImages.length > 0;
    const galleryWithSrc = galleryImages.filter((item) => item?.src);
    const previewSrc = src || galleryWithSrc[index]?.src || galleryWithSrc[0]?.src;
    if (!previewSrc && !hasLazyGallery) return;
    setPreview({
      open: true,
      src: previewSrc || '',
      title: title || alt || 'Image Preview',
      alt: alt || title || 'Image Preview',
      images: hasLazyGallery ? galleryImages : galleryWithSrc,
      index,
      employeeName,
      employeeId,
      employeeImage,
      hideTitle,
      resolveImageSrc: hasLazyGallery ? resolveImageSrc : null,
    });
  }, []);

  const handleDocumentClick = useCallback((event) => {
    const img = event.target?.closest?.('img');
    if (!img || shouldSkipImagePreview(img)) return;

    event.preventDefault();
    event.stopPropagation();
    const group = collectImageGroup(img);
    const selectedImage = group.images[group.index] || {};
    openImagePreview({
      src: img.currentSrc || img.src,
      title: selectedImage.title || getImageTitle(img),
      alt: selectedImage.alt || img.getAttribute('alt') || getImageTitle(img),
      images: group.images,
      index: group.index,
    });
  }, [openImagePreview]);

  useEffect(() => {
    document.addEventListener('click', handleDocumentClick, true);
    return () => {
      document.removeEventListener('click', handleDocumentClick, true);
    };
  }, [handleDocumentClick]);

  const contextValue = useMemo(() => ({
    openImagePreview,
    closeImagePreview,
  }), [closeImagePreview, openImagePreview]);

  return (
    <ImagePreviewContext.Provider value={contextValue}>
      {children}
      <ImagePreviewDialog
        open={preview.open}
        src={preview.src}
        title={preview.title}
        alt={preview.alt}
        images={preview.images}
        initialIndex={preview.index}
        employeeName={preview.employeeName}
        employeeId={preview.employeeId}
        employeeImage={preview.employeeImage}
        hideTitle={preview.hideTitle}
        resolveImageSrc={preview.resolveImageSrc}
        onClose={closeImagePreview}
      />
    </ImagePreviewContext.Provider>
  );
};

export const useImagePreview = () => {
  const context = useContext(ImagePreviewContext);
  if (!context) {
    throw new Error('useImagePreview must be used within ImagePreviewProvider');
  }
  return context;
};

export default ImagePreviewDialog;
